<template>
  <div class="desktop-confirm-section">
    <div class="desktop-confirm-header">
      <h3 class="desktop-confirm-title">3. {{ $t('detail.confirmEntries') }}</h3>
    </div>
    
    <div v-if="lines.length === 0" class="desktop-no-entries">
      {{ $t('detail.noEntriesYet') }}
    </div>
    
    <div v-else class="desktop-lines-section">
      <div class="desktop-lines-header">
        <span>{{ $t('detail.yourEntries') }} ({{ lines.length }})</span>
        <button class="desktop-clear-btn" @click="clearAll">{{ $t('common.clearAll') }}</button>
      </div>
      
      <div class="desktop-lines-list">
        <div
          v-for="(line, index) in lines"
          :key="index"
          class="desktop-line-item"
        >
          <div class="desktop-line-content">
            <div class="desktop-line-numbers">
              <div class="desktop-line-balls">
                <div
                  v-for="number in line.numbers"
                  :key="number"
                  class="desktop-line-ball"
                >
                  {{ number }}
                </div>
                <div v-if="line.bonus" class="desktop-line-bonus">
                  <span class="desktop-line-bonus-separator">+</span>
                  <div class="desktop-line-bonus-ball">
                    {{ line.bonus }}
                  </div>
                </div>
              </div>
            </div>
            <div class="desktop-line-odds">
              {{ formatLineOdds(line) }}
            </div>
          </div>
          <button
            class="desktop-remove-line-btn"
            @click="removeLine(index)"
          >
            ×
          </button>
        </div>
      </div>
      
      <button
        class="desktop-add-to-betslip-btn"
        @click="addToBetslip"
        :disabled="lines.length === 0 || isProcessing"
      >
        {{ $t('detail.addToBetslip') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, withDefaults } from 'vue';
import { formatOdds } from '../../utils/currency';

interface Line {
  numbers: number[];
  bonus?: number;
  numBets: number;
  picks: number;
  market?: {
    marketGroup: {
      group_id: number;
      name: string;
      slug: string;
      group_ordering: number;
      has_bonus_ball: boolean;
      markets: Array<{
        market_id: number;
        market_name: string;
        market_slug: string;
        market_description: string;
        market_additional_info: string;
        market_ordering: number;
        market_active: boolean;
        outcomes: Array<{
          outcome_id: number;
          text: string;
          select_numbers: boolean;
          pick: number;
          slug: string;
          odds: string;
          true_odds: string;
          probability: string;
          specifier: string | null;
          market_outcomes_id: number;
        }>;
      }>;
      ballset: {
        rules: Array<{
          id: number;
          name: string;
          balls_min: number;
          balls_max: number;
          picks_min: number;
          picks_max: number;
          has_bonus: number;
          max_resulted: number;
          type: string;
        }>;
      };
    };
    market: {
      market_id: number;
      market_name: string;
      market_slug: string;
      market_description: string;
      market_additional_info: string;
      market_ordering: number;
      market_active: boolean;
      outcomes: Array<{
        outcome_id: number;
        text: string;
        select_numbers: boolean;
        pick: number;
        slug: string;
        odds: string;
        true_odds: string;
        probability: string;
        specifier: string | null;
        market_outcomes_id: number;
      }>;
    };
    label: string;
    odds: number;
    select_numbers: boolean;
    balls: number;
    outcome_id: string;
  };
}

interface Props {
  lines: Line[];
  currentGameSlug?: string;
  selectedRelatedGames?: string[];
  isProcessing?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  selectedRelatedGames: () => [],
  isProcessing: false,
});

const emit = defineEmits<{
  'clear-all': [];
  'remove-line': [index: number];
  'add-to-betslip': [lines: Line[]];
  'update:selectedRelatedGames': [games: string[]];
}>();

const getLineOdds = (line: Line): number | null => {
  if (line.market) {
    return line.market.odds;
  }
  return null;
};

const formatLineOdds = (line: Line): string => {
  const odds = getLineOdds(line);
  return odds ? formatOdds(odds) : '1.00';
};

const removeLine = (index: number) => {
  emit('remove-line', index);
};

const clearAll = () => {
  emit('clear-all');
};

const addToBetslip = () => {
  emit('add-to-betslip', props.lines);
};
</script>

<script lang="ts">
export default {
  name: "DesktopConfirmSection",
};
</script>

<style scoped>
.desktop-confirm-section {
  background: var(--bg-view-header, var(--bg-secondary));
  border-radius: 12px;
  margin-top: 16px;
  overflow: hidden;
}

.desktop-confirm-header {
  padding: 16px 20px;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
}

.desktop-confirm-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.desktop-no-entries {
  padding: 20px;
  text-align: center;
  font-size: 14px;
  color: var(--text-secondary);
  font-style: italic;
}

.desktop-lines-section {
  background: transparent;
  border-radius: 0;
  padding: 16px 20px;
}

.desktop-lines-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.desktop-lines-header span {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.desktop-clear-btn {
  background: none;
  border: none;
  color: var(--accent-color);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: opacity 0.2s ease;
}

.desktop-clear-btn:hover {
  opacity: 0.8;
}

.desktop-lines-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.desktop-line-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.desktop-line-item:hover {
  border-color: var(--accent-color);
}

.desktop-line-content {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.desktop-line-numbers {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.desktop-line-balls {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.desktop-line-ball {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--primary);
  color: var(--text-ball-selected, white);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  border: 2px solid var(--secondary);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.desktop-line-bonus {
  display: flex;
  align-items: center;
  gap: 6px;
}

.desktop-line-bonus-separator {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-right: 2px;
}

.desktop-line-bonus-ball {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--text-ball-selected) 0%, var(--bg-secondary) 100%);
  color: var(--accent-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  border: 2px solid var(--accent-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.desktop-line-odds {
  font-size: 14px;
  font-weight: 600;
  color: var(--accent-color);
}

.desktop-remove-line-btn {
  background: var(--bg-danger);
  border: none;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;
  transition: all 0.2s ease;
}

.desktop-remove-line-btn:hover {
  background: var(--bg-danger-hover);
  transform: scale(1.1);
}

.desktop-add-to-betslip-btn {
  width: 100%;
  padding: 16px;
  background: var(--accent-color);
  color: var(--text-button, white);
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;
}

.desktop-add-to-betslip-btn:hover:not(:disabled) {
  background: var(--bg-button-hover);
}

.desktop-add-to-betslip-btn:disabled {
  background: var(--bg-button-disabled);
  cursor: not-allowed;
  opacity: 0.6;
}
</style>

